/**
 * PendingCouponBanner
 * Shown globally when a logged-in user has unused targeted coupons.
 * Fetches via edge function (never direct DB) — code values are safe to return
 * since they are locked to this specific user server-side.
 */
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Tag, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";

type PendingCoupon = {
  id: string;
  code: string;
  discount_percent: number;
  applies_to: string;
  shop_label: string;
  shop_path: string;
  expires_at: string | null;
};

export function PendingCouponBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const { data } = useQuery({
    queryKey: ["pending-user-coupons", user?.id],
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 min
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("get-user-coupons");
      if (error) throw error;
      return (data?.coupons ?? []) as PendingCoupon[];
    },
  });

  const visible = (data ?? []).filter((c) => !dismissed.has(c.id));

  if (!user || visible.length === 0) return null;

  return (
    <div className="space-y-2 px-4 pt-2">
      {visible.map((coupon) => {
        const magicLink = `${coupon.shop_path}?coupon=${encodeURIComponent(coupon.code)}`;
        const expiry = coupon.expires_at
          ? ` · Expires ${format(parseISO(coupon.expires_at), "MMM d")}`
          : "";

        return (
          <div
            key={coupon.id}
            className="flex items-center gap-3 rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 text-sm"
          >
            <Tag className="h-4 w-4 text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-semibold text-foreground">
                You have a {coupon.discount_percent}% personal coupon!
              </span>
              <span className="text-muted-foreground ml-1.5">
                {coupon.shop_label}{expiry}
              </span>
            </div>
            <Link
              to={magicLink}
              className="flex items-center gap-1 text-primary font-medium text-xs hover:underline shrink-0"
            >
              Shop now <ArrowRight className="h-3 w-3" />
            </Link>
            <button
              className="text-muted-foreground hover:text-foreground shrink-0"
              onClick={() => setDismissed((prev) => new Set([...prev, coupon.id]))}
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
